import { Box } from '@mui/material';
import { NetworkStatus } from '@apollo/client';
import { forwardRef, useEffect, useRef } from 'react';

import type {
  FilterGroup,
  MultiGroupFilterServerSideSearch,
} from './MultiGroupFilter';
import { MultiGroupFilterOptionSkeleton } from './MultiGroupFilterOptionSkeleton';

export interface MultiGroupFilterListboxConfiguration {
  groupOptions?: FilterGroup[];
  selectedOptions?: FilterGroup[];
  onSelectedOptionsChange?: (selectedOptions: FilterGroup[]) => void;
  serverSideSearch?: MultiGroupFilterServerSideSearch;
}

export interface MultiGroupFilterListboxProps extends React.ComponentProps<'div'> {
  children?: React.ReactNode;
  groupOptions?: FilterGroup[];
  serverSideSearch?: MultiGroupFilterServerSideSearch;
}

// Start the next page while the last few options are still scrolling into
// view, so the list keeps moving instead of stalling at the bottom.
const FETCH_MORE_SCROLL_OFFSET = 160;

// Group names vary in length, so the placeholders do too rather than reading
// as one uniform block.
const SKELETON_NAME_WIDTHS = ['70%', '45%', '60%', '80%', '50%'];

export const MultiGroupFilterListbox = forwardRef<
  HTMLDivElement,
  MultiGroupFilterListboxProps
>(({ children, groupOptions, serverSideSearch, onScroll, ...other }, ref) => {
  const fetchingMoreOptions =
    serverSideSearch?.networkStatus === NetworkStatus.fetchMore;

  const placeholderOptionsCount = fetchingMoreOptions
    ? Math.min(
        serverSideSearch?.pageSize ?? 0,
        serverSideSearch?.remainingOptionsCount ?? 0,
      )
    : 0;

  const scrollerRef = useRef<HTMLDivElement | null>(null);

  // A scroll event can fire again before Apollo reports the request, so the
  // page is only asked for once while it is in flight.
  const requestedMoreOptions = useRef(false);

  useEffect(() => {
    if (fetchingMoreOptions) {
      return;
    }

    // Cleared on completion rather than when the option count changes: a page
    // made up entirely of groups already appended to the options leaves that
    // count the same, which would block paging for good.
    requestedMoreOptions.current = false;

    const scroller = scrollerRef.current;

    if (!scroller || !serverSideSearch?.hasNextPage) {
      return;
    }

    // With the selection filtered out of the options, a page can leave the
    // list too short to scroll, and then no scroll event can ask for the
    // next one. Keep going until it scrolls or the pages run out.
    if (scroller.scrollHeight > scroller.clientHeight) {
      return;
    }

    requestedMoreOptions.current = true;
    serverSideSearch.fetchMore();
  });

  // MUI moves the highlighted option into view by writing to the element it
  // holds as the listbox, which is not the element scrolling here, so arrow
  // keys have to be followed through to this one. Only on a change of
  // highlight, so that an appended page does not move the list.
  const focusedOptionId = useRef<string | null>(null);

  useEffect(() => {
    const scroller = scrollerRef.current;

    if (!scroller) {
      return;
    }

    const focusedOption = scroller.querySelector('[role="option"].Mui-focused');

    if ((focusedOption?.id ?? null) === focusedOptionId.current) {
      return;
    }

    focusedOptionId.current = focusedOption?.id ?? null;
    focusedOption?.scrollIntoView({ block: 'nearest' });
  });

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    onScroll?.(event);

    const listbox = event.currentTarget;
    const nearBottom =
      listbox.scrollHeight - listbox.scrollTop <=
      listbox.clientHeight + FETCH_MORE_SCROLL_OFFSET;

    if (
      !nearBottom ||
      !serverSideSearch?.hasNextPage ||
      fetchingMoreOptions ||
      requestedMoreOptions.current
    ) {
      return;
    }

    requestedMoreOptions.current = true;
    serverSideSearch.fetchMore();
  };

  const options = (
    <>
      {children}
      {Array.from({ length: placeholderOptionsCount }, (_, index) => (
        <MultiGroupFilterOptionSkeleton
          key={index}
          nameWidth={SKELETON_NAME_WIDTHS[index % SKELETON_NAME_WIDTHS.length]}
        />
      ))}
    </>
  );

  // A caller without pagination keeps MUI's listbox as the scroller, which
  // also keeps MUI's keyboard scrolling.
  if (!serverSideSearch) {
    return (
      <div ref={ref} {...other} role="listbox" onScroll={handleScroll}>
        {options}
      </div>
    );
  }

  // MUI writes to the listbox's scrollTop whenever the options change, which
  // for a paginated list means every appended page. Scrolling an element of
  // our own inside the listbox puts the position out of its reach, since
  // appending children never moves a scroll container on its own.
  return (
    <div ref={ref} {...other} role="listbox">
      <Box
        ref={scrollerRef}
        onScroll={handleScroll}
        // Matches the height MUI would have given its own listbox, which is
        // handed over here so this element does the scrolling.
        sx={{ maxHeight: '40vh', overflowY: 'auto' }}
      >
        {options}
      </Box>
    </div>
  );
});

MultiGroupFilterListbox.displayName = 'MultiGroupFilterListbox';
